import { useCallback, useContext } from 'react';
import { FieldUsageEntities } from '../types';
import DashboardsContext from '../../../../../../contexts/DashboardsContext';

const useGetFieldDashboardUsage = () => {
  const { allDashboards } = useContext(DashboardsContext);

  const getFieldDashboardUsage = useCallback(
    (usage: FieldUsageEntities): DashboardType[] => {
      if (!usage) return [];

      const chartDefIds = new Set<string>();
      const gadgetIds = new Set<string>();
      const metricListIds = new Set<string>();
      const explicitDashboardIds = new Set<string>();

      // VisualisationDefinitions (chart definitions) that reference the field
      usage.gadgetDimensions?.forEach((d) => d?.id && chartDefIds.add(d.id));
      usage.simpleGrids?.forEach((d) => d?.id && chartDefIds.add(d.id));
      usage.simpleGridSorts?.forEach((d) => d?.id && chartDefIds.add(d.id));

      // Dashboard gadgets that reference the field (via filters)
      usage.dashboardGadgetFilters?.forEach(
        (g) => g?.id && gadgetIds.add(g.id),
      );

      // Metric lists that reference the field
      usage.metricLists?.forEach((ml) => {
        if (ml?.id) metricListIds.add(ml.id);
        if (ml?.dashboardId) explicitDashboardIds.add(ml.dashboardId);
      });

      const matchedDashboardIds = new Set<string>(explicitDashboardIds);

      allDashboards.forEach((d) => {
        const cards = d.canvas?.cards || [];
        const hasMatch = cards.some((c) => {
          const content = c?.content;
          if (!content) return false;

          // Chart Definition cards
          if (
            content?.type === 'Chart Definition' &&
            content?.chartDefinitionId &&
            chartDefIds.has(content.chartDefinitionId)
          ) {
            return true;
          }

          // Dashboard Gadget cards
          if (
            content?.type === 'Dashboard Gadget' &&
            content?.dashboardGadgetId &&
            gadgetIds.has(content.dashboardGadgetId)
          ) {
            return true;
          }

          // Metric List cards
          if (
            content?.type === 'Metric List' &&
            content?.metricListId &&
            metricListIds.has(content.metricListId)
          ) {
            return true;
          }

          return false;
        });

        if (hasMatch) matchedDashboardIds.add(d.id);
      });

      // Return the DashboardType objects in the order of allDashboards
      return allDashboards.filter((d) => matchedDashboardIds.has(d.id));
    },
    [allDashboards],
  );

  return getFieldDashboardUsage;
};

export default useGetFieldDashboardUsage;
